import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Button, Container, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { portalTheme, brand } from './portalTheme';
import { BrandMark, PortalOutletContext } from './PortalUi';
import {
  clearCustomerSession,
  fetchNotifications,
  fetchProfile,
  storedCustomerName,
} from '../../../services/request/CustomerPortal';
import { CUSTOMER_LOGIN } from '../../../services/constants/URIConstants';
import { LEAD_SERVICE_HOST } from '../../../services/constants/RestConstants';
import { ReleasepilotAffiliation } from '../../../shared-components/branding/ReleasepilotAffiliation';
import { LanguageSwitcher, TranslationKey, useTranslation } from '../../../i18n';

const NAV: { to: string; label: TranslationKey }[] = [
  { to: '/customer/dashboard', label: 'portal.layout.nav.home' },
  { to: '/customer/debits', label: 'portal.layout.nav.debits' },
  { to: '/customer/payments', label: 'portal.layout.nav.payments' },
  { to: '/customer/deposits', label: 'portal.layout.nav.deposits' },
  { to: '/customer/products', label: 'portal.layout.nav.products' },
  { to: '/customer/notifications', label: 'portal.layout.nav.notifications' },
  { to: '/customer/profile', label: 'portal.layout.nav.profile' },
  { to: '/customer/help', label: 'portal.layout.nav.help' },
];

export function LocalTestingBanner() {
  const { t } = useTranslation();
  if (process.env.NODE_ENV === 'production') return null;
  return (
    <Box sx={{ bgcolor: brand.gold, color: brand.navy, px: 2, py: 0.75, textAlign: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {t('portal.layout.localTesting', { host: LEAD_SERVICE_HOST })}
      </Typography>
    </Box>
  );
}

export default function CustomerPortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [customerName, setCustomerName] = useState<string | undefined>(storedCustomerName());
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(() => {
    fetchNotifications()
      .then(items => setUnread(items.filter(item => !item.read).length))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    fetchProfile()
      .then(profile => setCustomerName(profile?.name ?? storedCustomerName()))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread, location.pathname]);

  useEffect(() => {
    const onFocus = () => refreshUnread();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUnread]);

  const signOut = () => {
    clearCustomerSession();
    navigate(CUSTOMER_LOGIN, { replace: true });
  };

  const context: PortalOutletContext = { customerName, refreshUnread };

  return (
    <ThemeProvider theme={portalTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: brand.ivory, display: 'flex', flexDirection: 'column' }}>
        <LocalTestingBanner />
        <Box component="header" sx={{ bgcolor: brand.navy, color: brand.ivory }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 2 }}>
            <Box
              component={NavLink}
              to="/customer/dashboard"
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'inherit', textDecoration: 'none' }}
            >
              <BrandMark size={42} />
              <Box>
                <Typography variant="overline" sx={{ color: brand.gold, lineHeight: 1.2, display: 'block' }}>
                  {t('portal.layout.brand')}
                </Typography>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>{t('portal.layout.product')}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: brand.mist, display: { xs: 'none', md: 'block' } }}>
                {customerName ?? t('portal.layout.customer')}
              </Typography>
              <LanguageSwitcher />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Logout />}
                onClick={signOut}
                sx={{ color: brand.ivory, borderColor: 'rgba(249,248,245,0.4)', '&:hover': { borderColor: brand.gold } }}
              >
                {t('portal.layout.signOut')}
              </Button>
            </Box>
          </Container>
          <Container maxWidth="lg">
            <Box component="nav" sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
              {NAV.map(item => (
                <Box
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === '/customer/dashboard'}
                  sx={{
                    px: 2,
                    py: 1.25,
                    whiteSpace: 'nowrap',
                    color: brand.mist,
                    textDecoration: 'none',
                    fontWeight: 600,
                    borderBottom: '3px solid transparent',
                    '&:hover': { color: brand.ivory },
                    '&.active': { color: brand.ivory, borderBottomColor: brand.gold },
                  }}
                >
                  {item.to === '/customer/notifications' ? (
                    <Badge color="secondary" variant="dot" invisible={unread === 0}>
                      <span style={{ paddingRight: 4 }}>{t(item.label)}</span>
                    </Badge>
                  ) : (
                    t(item.label)
                  )}
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
        <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
          <Outlet context={context} />
        </Container>
        <Box component="footer" sx={{ borderTop: `1px solid ${brand.border}`, py: 2 }}>
          <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center' }}>
            <ReleasepilotAffiliation compact />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
